/*
 * Source: https://codesandbox.io/s/reakit-tooltip-following-mouse-7wolg?file=/src/useCursorTooltipState.js:0-2274
 * with following changes:
 *   - added types
 *   - usePopoverState instead of useDialogState
 *   - added offset modifier to support gutter
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { type TooltipState, useTooltipState } from 'ariakit/tooltip'
import Popper from 'popper.js'

type MouseRef = Pick<Element, 'getBoundingClientRect'>

type useCursorTooltipStateProps = Partial<TooltipState> & {
    placement: TooltipState['placement']
}

export function useCursorTooltipState({
    placement: originalPlacement,
    ...rest
}: useCursorTooltipStateProps): TooltipState & { transform: string } {
    const popper = useRef<Popper>()
    const anchorRef = useRef<HTMLElement | null>(null)
    const mouseRef = useRef<MouseRef | null>(null)
    const popoverRef = useRef(null)

    const [placement, setPlacement] = useState(originalPlacement)
    const [transform, setTransform] = useState('translate3d(0,0,0)')

    // remove modal and setModal from state as it's not used in the tooltip
    const dialog = useTooltipState(rest)
    const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

    const createPopper = useCallback(() => {
        if ((mouseRef.current || anchorRef.current) && popoverRef.current) {
            popper.current = new Popper(
                (mouseRef.current as Element) || anchorRef.current,
                popoverRef.current,
                {
                    placement: originalPlacement,
                    eventsEnabled: dialog.open,
                    modifiers: {
                        applyStyle: { enabled: false },
                        updateStateModifier: {
                            order: 900,
                            enabled: true,
                            fn: (data) => {
                                setPlacement(data.placement as TooltipState['placement'])
                                const transform = data.styles.transform || 'translate3d(0,0,0)'
                                setTransform(transform)

                                return data
                            },
                        },
                        offset: {
                            offset: `0, ${rest.gutter}`,
                        },
                    },
                },
            )
        }
    }, [originalPlacement, dialog, rest.gutter])

    useIsomorphicLayoutEffect(() => {
        createPopper()
        return () => {
            if (popper.current) {
                popper.current.destroy()
            }
        }
    }, [createPopper])

    useEffect(() => {
        const reference = anchorRef.current

        const onMouseMove = (event: MouseEvent) => {
            if (reference) {
                mouseRef.current = {
                    getBoundingClientRect: () => ({
                        ...reference.getBoundingClientRect(),
                        top: event.clientY,
                        bottom: event.clientY,
                        left: event.clientX,
                        right: event.clientX,
                    }),
                }
                createPopper()
            }
        }
        if (reference) {
            reference.addEventListener('mousemove', onMouseMove)
        }
        return () => {
            mouseRef.current = null
            if (reference) {
                reference.removeEventListener('mousemove', onMouseMove)
            }
        }
    }, [createPopper])

    return {
        ...dialog,
        placement,
        anchorRef,
        popoverRef,
        transform,
    }
}
